import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Backdrop,
  Icon,
  PurchaseSummary,
  Spinner,
  Text
} from '@cencosud-cencommerce/paris-uikit';
import classNames from 'classnames';

import { DeliveryCard } from '@/Application/Containers';
import type { DeliveryCardProps } from '@/Application/Containers/DeliveryCard/interfaces';
import useKeyPress from '@/Application/Hooks/useKeyPress/useKeyPress';
import { MultiDeliveryModal, OutOfStockModal } from '@/Application/Modals';
import DeleteCartItemModal from '@/Application/Modals/DeleteCartItemModal';
import { outOfStockItems } from '@/Application/Modals/OutOfStockModal/outOfStockItems';
import { useCart } from '@/Application/Selectors/cart/useCart';
import {
  DEFAULT_INTERNAL_COLLECT_ALIAS,
  QUERY_PARAMS,
  URL_ROUTES
} from '@/constants';
import { trackEvent } from '@/Infrastructure/Utils/analytics/braze/brazeClient';
import { getOrderCompletedOnlineProduct } from '@/Infrastructure/Utils/analytics/braze/brazeProductTransformer';
import { syncCartItemsUserAttribute } from '@/Infrastructure/Utils/analytics/braze/cartItems/syncCartItemsUserAttribute';
import type { Cart } from '@/modules/cart/domain/Cart';
import environment from '@/modules/cart/environments/environment';
import {
  cartToCartData,
  deliveryInfoToShippingData
} from '@/modules/cart/infrastructure/mappers/cartToCartData';
import { CustomerTags } from '@/modules/customer/domain/Customer';
import { redirectToUrl } from '@/modules/perseo-billing/utils/window';
import { priceDtoToPriceString } from '@/modules/shared/mappers/priceDtoToPriceString';
import {
  getQueryParam,
  isDeviceAndroid,
  isDeviceIos
} from '@/modules/shared/utils/deviceDetections';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import { useSendShippingEvents } from '@/modules/shared/utils/gtmEvents/events/useSendShippingEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import type {
  DeliveryGroup,
  DeliveryProduct
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type { HttpClientPreferenceParams } from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import ErrorPage from '@/Pages/ShippingPage/ErrorPage/index';
import type { ShippingMethodLineItem } from '@/Pages/ShippingPage/ShippingPageContext';
import ShippingPageContext, {
  useShippingPageContext
} from '@/Pages/ShippingPage/ShippingPageContext';
import useShippingPageInitializer from '@/Pages/ShippingPage/useShippingPageInitializer';
import { AddressMain } from '@/sections/address/components/AddressMain';
import { useCreateShippingAddress } from '@/sections/address/hooks/mutators/useCreateShippingAddress';
import { useDefaultAddress } from '@/sections/address/hooks/queries/useDefaultAddress';
import OutOfStockProducts from '@/sections/cart/components/OutOfStock/OutOfStockProducts';
import useRemoveLineItem from '@/sections/cart/hooks/mutators/useRemoveLineItem';
import useRemoveOutOfStockItems from '@/sections/cart/hooks/mutators/useRemoveOutOfStockItems';
import useCartPayments from '@/sections/cart/hooks/useCartPayments';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import { useKiosco } from '@/sections/shared/hooks/useKiosco';
import { AddToCartButton } from '@/sections/shared/mocks/addToCartButton';
import { CustomerSignInButton } from '@/sections/shared/mocks/customerLogin';
import { GuestSignInButton } from '@/sections/shared/mocks/guestSignInButton';
import {
  getCollectShippingAddress,
  isShippingAddressComplete,
  verifyAllGroupsArePickup
} from '@/sections/shared/utils';
import verifyHasSomeHomeDelivery from '@/sections/shared/utils/verifyHasSomeHomeDelivery';
import useGetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useGetGeneralShippingMethods';
import useSetGeneralShippingMethods from '@/sections/shippingMethods/hooks/useSetGeneralShippingMethods';
import { formatDate } from '@/sections/shippingMethods/mappers/shippingMethodMapper';

export enum MODALS {
  EDIT_ADDRESS,
  CREATE_ADDRESS
}

const getBillingFinalUrl = () => {
  const device = getQueryParam(QUERY_PARAMS.DEVICE);
  if (!device || device === 'web') return URL_ROUTES.BILLING;
  return `${URL_ROUTES.BILLING}?device=${device}`;
};

const BILLING_URL = getBillingFinalUrl();

const handleEditCart = () => {
  SendShippingEvents.sendEvent({
    event: 'baseEvent',
    extraFields: {
      'event-interactive-category':
        ShippingEventInteractiveCategories.CheckoutSummary,
      'event-interactive-tag': ShippingEventInteractiveTags.EditCart
    }
  });
  redirectToUrl(URL_ROUTES.CART);
};

const ShippingPageContent = () => {
  useShippingPageInitializer();
  useKiosco();
  const navigate = useNavigate();
  const [retry, setRetry] = useState<number>(0);
  const {
    data: cart,
    isLoading: isCartLoading,
    refetch: refetchCart,
    isError: isCartError
  } = useCart();
  const hasSetDefaultShippingOptions = useRef(false);
  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const {
    shippingMethods,
    unavailable,
    isLoading: isShippingLoading,
    isError: isShippingError,
    refetchShippingMethods
  } = useGetGeneralShippingMethods();

  const [otherError, setOtherError] = useState<boolean>(false);

  const {
    setDefaultShippingOptions,
    error: errorSetShippingMethod,
    handleSetError: handleSetShippingOptionError,
    isLoading: isSettingShippingMethod
  } = useSetGeneralShippingMethods();

  const {
    cartPayments,
    isLoading: isCartPaymentsLoading,
    error: cartPaymentsError
  } = useCartPayments(cart);
  const {
    customer: { data: customer, isLoading: isCustomerLoading }
  } = useCustomerContext();

  const isColab = !!customer?.customerTags?.includes(
    CustomerTags.CENCOSUD_EMPLOYEE
  );
  const customerCollab = {
    isColabCustomer: isColab
  };

  const { data: defaultAddress } = useDefaultAddress();
  const { trigger: removeLineItem, isLoading: removingLineItem } =
    useRemoveLineItem();
  const { trigger: removeOutOfStockItems, isLoading: removingOutOfStockItem } =
    useRemoveOutOfStockItems();
  const {
    getSkusHistory,
    getSkuHistory,
    updateSkuHistory,
    removeSkuFromHistory,
    completeSkuHistory
  } = useShippingPageContext();
  const queryParams = new URLSearchParams(window.location.search);
  const hasDefaultShippingAddress =
    Boolean(defaultAddress?.alias) &&
    defaultAddress?.alias !== DEFAULT_INTERNAL_COLLECT_ALIAS;
  const hasAddresses =
    Boolean(customer?.addresses && customer.addresses.length > 0) ||
    hasDefaultShippingAddress;

  const [scheduledDate, setScheduledDate] = useState<string | undefined>(
    undefined
  );

  const { trigger: createShippingAddress } = useCreateShippingAddress(false);

  const [modalOpen, setModalOpen] = useState<MODALS | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [isModalMDOpen, setIsModalMDOpen] = useState(false);
  const [productToRemove, setProductToRemove] = useState<
    DeliveryProduct | undefined
  >();
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(
    Boolean(queryParams.get('showOutOfStock'))
  );

  const { VITE_BASE_ENVIRONMENT, Environments, MAX_RETRY } = environment;

  const otherMethodsPrice = cartPayments?.otherMethod?.centAmount
    ? priceDtoToPriceString(cartPayments.otherMethod)
    : undefined;
  let cencoPayPrice = undefined;
  let cencosudCardPrice = undefined;

  const cencosudAmount = cartPayments?.cencosudCard?.centAmount;
  const cencoPayAmount = cartPayments?.cencoPay?.centAmount;
  const hasBothCartPayments = cencosudAmount && cencoPayAmount;

  if (hasBothCartPayments && cencoPayAmount < cencosudAmount) {
    cencoPayPrice = priceDtoToPriceString(cartPayments.cencoPay);
  }

  if (hasBothCartPayments && cencoPayAmount >= cencosudAmount) {
    cencosudCardPrice = priceDtoToPriceString(cartPayments.cencosudCard);
  }

  const shippingData = deliveryInfoToShippingData(cart?.deliveryInfo);

  const { onKeyDown } = useKeyPress(() => setIsModalMDOpen(!isModalMDOpen));

  const isOnlyIntangibleGroups = Boolean(
    shippingMethods?.deliveryGroups.length &&
      !shippingMethods?.deliveryGroups.find((group) => !group.intangible)
  );

  const generalShippingMethods = shippingMethods?.deliveryGroups?.map(
    (group: DeliveryGroup) => {
      const productList = group.productList.map((product: DeliveryProduct) => ({
        ...product,
        onDelete: () => {
          setProductToRemove(product);
        }
      }));
      const scheduledOption = group.shippingOptions?.find(
        (option) => option.shippingMethod === 'SCHEDULED'
      );
      if (scheduledDate && scheduledOption?.selected) {
        const skuHistory = getSkuHistory(productList[0].productSKU);

        const date = new Date(
          skuHistory.clientPreference?.[0]?.deliveryDateFrom ?? ''
        );

        const hasHistory = !!skuHistory.clientPreference?.[0]?.deliveryDateFrom;

        group.shippingOptions = group.shippingOptions?.map((option) =>
          option.shippingMethod === 'SCHEDULED'
            ? {
                ...option,
                date: hasHistory ? formatDate(date) : scheduledOption?.date
              }
            : option
        );
      }

      return {
        ...group,
        productList,
        shippingOptions: group.shippingOptions
      };
    }
  );

  const handleSelectClientSelection = useCallback(
    async (
      deliveryGroupId: string,
      params?: HttpClientPreferenceParams,
      scheduledDate?: string
    ) => {
      const group = generalShippingMethods?.find(
        (shippingMethod) => shippingMethod.id === deliveryGroupId
      );

      const otherGroups = generalShippingMethods?.filter(
        (shippingMethod) => shippingMethod.id !== deliveryGroupId
      );
      const otherSkusList =
        otherGroups?.flatMap((group) =>
          group.productList.map((product) => product.productSKU)
        ) ?? [];
      const otherSkusData = getSkusHistory(otherSkusList);

      const skus = group?.productList.map((product) => product.productSKU);

      const dataToSend = skus?.map((sku) => {
        const history = getSkuHistory(sku);

        if (!params && group?.shippingOptions?.[0]) {
          const defaultDeliveryOption = group?.shippingOptions[0];
          params = {
            deliveryDateFrom:
              defaultDeliveryOption?.deliveryDateFrom?.split('T')[0],
            deliveryDateTo:
              defaultDeliveryOption?.deliveryDateTo?.split('T')[0],
            shippingMethod: defaultDeliveryOption.shippingMethod,
            shippingSubType: defaultDeliveryOption.shippingSubType,
            shippingType: defaultDeliveryOption.shippingType
          };
        }

        const data = {
          sku,
          clientPreference: params
            ? [params, ...(history.clientPreference ?? [])]
            : history.clientPreference
        };
        updateSkuHistory(data);
        return data;
      }) as ShippingMethodLineItem[];

      await refetchShippingMethods([...dataToSend, ...otherSkusData]);

      if (scheduledDate) {
        setScheduledDate(scheduledDate);
      } else {
        setScheduledDate(undefined);
      }
      hasSetDefaultShippingOptions.current = false;
    },
    [
      refetchShippingMethods,
      generalShippingMethods,
      getSkuHistory,
      getSkusHistory,
      updateSkuHistory
    ]
  );

  const isShippingPageLoading =
    isShippingLoading ||
    isCustomerLoading ||
    isCartLoading ||
    removingOutOfStockItem ||
    isCartPaymentsLoading;

  const [isShippingPageError, setIsShippingPageError] = useState<boolean>(
    Boolean(isShippingError) ||
      Boolean(isCartError) ||
      Boolean(cartPaymentsError) ||
      Boolean(errorSetShippingMethod) ||
      otherError
  );

  const handleSetOtherError = (value: boolean) => {
    setOtherError(value);
  };

  const handleSetModals = (modal: number | null, showAlert?: boolean) => {
    if (showAlert) {
      setShowAlert(true);
    }
    if (!modal) {
      setShowAlert(false);
      setInitialLoad(false);
    }
    setModalOpen(modal);
  };

  useEffect(() => {
    if (isOnlyIntangibleGroups) {
      const billingFinalUrl = getBillingFinalUrl();
      navigate(billingFinalUrl);
    }
    if (
      !hasDefaultShippingAddress &&
      !hasAddresses &&
      !isCustomerLoading &&
      !isShippingLoading &&
      initialLoad
    ) {
      setShowAlert(true);
      setModalOpen(MODALS.CREATE_ADDRESS);
    } else {
      setShowAlert(false);
    }
  }, [
    initialLoad,
    hasDefaultShippingAddress,
    hasAddresses,
    isCustomerLoading,
    isShippingLoading,
    isOnlyIntangibleGroups,
    navigate
  ]);

  useEffect(() => {
    if (
      (cart?.lineItems?.length && shippingMethods?.deliveryGroups?.length) ||
      (shippingMethods &&
        !shippingMethods.deliveryGroups.length &&
        unavailable?.length)
    ) {
      if (!hasSetDefaultShippingOptions.current) {
        setDefaultShippingOptions(shippingMethods.deliveryGroups);
        hasSetDefaultShippingOptions.current = true;
      }
    }
  }, [
    cart?.lineItems,
    unavailable?.length,
    setDefaultShippingOptions,
    shippingMethods,
    hasSetDefaultShippingOptions
  ]);

  const handleSetDefaultOptions = () => {
    hasSetDefaultShippingOptions.current = false;
  };

  useEffect(() => {
    if (
      isShippingPageLoading ||
      isSummaryOpen ||
      modalOpen ||
      !!productToRemove
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [isShippingPageLoading, isSummaryOpen, modalOpen, productToRemove]);

  const sendClickEvent = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.ClickControledError,
        'event-interactive-tag': 'Volver a intentar'
      }
    });
  };

  const handleRefetch = async () => {
    if (retry < 3) {
      sendClickEvent();
      await refetchCart();
      await refetchShippingMethods(completeSkuHistory);
      handleSetShippingOptionError(undefined);
      handleSetOtherError(false);
      hasSetDefaultShippingOptions.current = false;
      setRetry((prevValue) => prevValue + 1);
    }
  };

  useEffect(() => {
    if (isShippingPageError && retry >= MAX_RETRY) {
      redirectToUrl(URL_ROUTES.CART);
    }
  }, [isShippingPageError, retry, MAX_RETRY]);

  useEffect(() => {
    setIsShippingPageError(
      Boolean(isShippingError) ||
        Boolean(isCartError) ||
        Boolean(cartPaymentsError) ||
        Boolean(errorSetShippingMethod) ||
        otherError
    );
  }, [
    isShippingError,
    isCartError,
    cartPaymentsError,
    errorSetShippingMethod,
    otherError
  ]);

  useEffect(() => {
    const hasCart = !!cart?.id;
    if (!hasCart) return;

    const hasOutOfStockItems = cart?.outOfStockItems?.length;
    if (hasOutOfStockItems) return;

    const isEmptyCart = cart?.lineItems?.length === 0;
    if (isEmptyCart) {
      redirectToUrl(URL_ROUTES.CART);
      return;
    }
  }, [cart, navigate]);

  const isWebView = isDeviceAndroid() || isDeviceIos();

  const { sendContinueGtmEvents } = useSendShippingEvents();

  if (isShippingPageError) {
    return (
      <>
        {VITE_BASE_ENVIRONMENT === Environments.Local && (
          <div className="co-flex co-gap-4 co-w-full co-p-4">
            <GuestSignInButton />
            <CustomerSignInButton />
            <AddToCartButton />
          </div>
        )}

        <ErrorPage variant="something-went-wrong" onClick={handleRefetch} />
      </>
    );
  }

  const isLoadingDeleteCartItem =
    removingLineItem ||
    isCartPaymentsLoading ||
    isShippingLoading ||
    isSettingShippingMethod;

  const hasDeliveryGroups = cart?.deliveryInfo?.deliveryGroups?.length ?? 0 > 0;

  const sendGroupsInformationModalEvent = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion': ShippingEventInteractiveActions.Click,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag': ShippingEventInteractiveTags.GroupsInformation
      }
    });
  };

  const validateAndCreateShippingForPickup = async (cart: Cart) => {
    try {
      if (!cart?.shippingAddress) return;

      const isAddressValid = isShippingAddressComplete(cart?.shippingAddress);

      if (!isAddressValid) {
        const collectShippingAddress = getCollectShippingAddress(
          cart,
          customer
        );
        await createShippingAddress(collectShippingAddress);
        navigate(BILLING_URL);
      }

      if (isAddressValid) {
        navigate(BILLING_URL);
      }
    } catch (error) {
      console.error('Error creating shipping address:', error);
    }
  };

  const hasOutOfStockItems = (cart?.outOfStockItems?.length ?? 0) > 0;
  const allProductsAreOutOfStock =
    cart?.lineItems?.length === 0 && hasOutOfStockItems;

  const allGroupsArePickup = cart ? verifyAllGroupsArePickup(cart) : false;
  const hasSomeHomeDelivery = cart ? verifyHasSomeHomeDelivery(cart) : false;

  return (
    <div>
      {isShippingPageLoading ? (
        <>
          <Backdrop
            className="co-bg-white co-opacity-40"
            show={isShippingPageLoading}
          />
          <div className="co-fixed co-left-0 co-top-0 co-h-full co-w-full co-flex co-justify-center co-items-center co-z-50">
            <Spinner />
          </div>
        </>
      ) : null}

      {/* TODO: remove this when its no longer needed */}
      {VITE_BASE_ENVIRONMENT === Environments.Local && (
        <div className="co-flex co-gap-4 co-w-full co-p-4">
          <GuestSignInButton />
          <CustomerSignInButton />
          <AddToCartButton />
        </div>
      )}

      <div
        className={classNames(
          'co-max-w-[1536px] co-w-screen co-grid co-gap-5 co-grid-cols-1 tablet_w:co-grid-cols-[1fr_375px] desktop:co-grid-cols-[1fr_430px]'
        )}
      >
        {/* Delivery */}
        <div
          className={classNames(
            'co-flex co-flex-col co-gap-5 co-px-4 tablet_w:co-px-0 tablet_w:co-pl-4',
            !isSummaryOpen && 'co-mb-[150px]'
          )}
        >
          {!isCartLoading && hasSomeHomeDelivery && (
            <AddressMain
              handleSetModals={handleSetModals}
              hasAddresses={hasAddresses}
              defaultAddress={defaultAddress}
              modalOpen={modalOpen}
              showAlert={showAlert}
              handleSetOptions={handleSetDefaultOptions}
            />
          )}
          {shippingMethods?.deliveryGroups &&
            shippingMethods?.deliveryGroups.length > 1 && (
              <div className="co-flex co-items-center co-gap-2">
                <Icon name="info-circle" width={20} height={20} />
                <Text
                  tabIndex={0}
                  color="primary-500"
                  variant="link2"
                  weight={600}
                  onKeyDown={onKeyDown}
                  onClick={() => {
                    setIsModalMDOpen(!isModalMDOpen);
                    sendGroupsInformationModalEvent();
                  }}
                >
                  ¿Por qué hay varias entregas?
                </Text>
              </div>
            )}

          <OutOfStockProducts />

          {generalShippingMethods?.map(
            (deliveryGroup: DeliveryGroup, index) => {
              return (
                <div
                  key={deliveryGroup.id}
                  className={classNames(
                    'co-w-full co-bg-white co-flex co-items-center co-justify-center',
                    'co-border co-rounded-lg co-border-neutral-300',
                    !isSummaryOpen ? 'co-mb-150px tablet_w:co-mb-4' : 'co-mb-4'
                  )}
                >
                  <DeliveryCard
                    {...(deliveryGroup as DeliveryCardProps)}
                    handleSetOptions={handleSetDefaultOptions}
                    cartShippingAddress={defaultAddress}
                    groupNumber={index + 1}
                    setOtherError={handleSetOtherError}
                    handleSelectClientOption={handleSelectClientSelection}
                  />
                </div>
              );
            }
          )}
        </div>
        <div className="tablet_w:co-pr-4">
          {/* Purchase Summary */}
          {cart ? (
            <PurchaseSummary
              className="co-z-10"
              isOpen={isSummaryOpen}
              onOpen={() => setIsSummaryOpen(!isSummaryOpen)}
              showCostsOnMobile={{
                showShippingTotal: true
              }}
              onContinue={async () => {
                sendContinueGtmEvents();

                if (cart?.outOfStockItems?.length) {
                  await removeOutOfStockItems(cart?.outOfStockItems);
                }

                if (allGroupsArePickup) {
                  await validateAndCreateShippingForPickup(cart);
                }

                if (hasDefaultShippingAddress && hasDeliveryGroups) {
                  navigate(BILLING_URL);
                } else {
                  setInitialLoad(true);
                  handleSetModals(MODALS.CREATE_ADDRESS, true);
                }
              }}
              continueButtonDisabled={allProductsAreOutOfStock}
              isVisibleEditButton={!isWebView}
              onEditCart={handleEditCart}
              cartData={cartToCartData(cart, customerCollab)}
              shippingData={{
                shippingItems: shippingData?.shippingItems ?? [],
                shippingCost: shippingData?.shippingCost ?? '',
                shippingTotal: shippingData?.shippingTotal ?? ''
              }}
              totalPrice={otherMethodsPrice}
              totalPriceCenco={cencosudCardPrice}
              totalPriceCencoPay={cencoPayPrice}
            />
          ) : null}
        </div>
      </div>
      <MultiDeliveryModal
        isOpen={isModalMDOpen}
        onClose={() => setIsModalMDOpen(false)}
      />
      <OutOfStockModal
        isOpen={isOutOfStockModalOpen}
        onClose={() => setIsOutOfStockModalOpen(false)}
        products={outOfStockItems}
      />
      <DeleteCartItemModal
        product={productToRemove}
        isOpen={!!productToRemove}
        loading={isLoadingDeleteCartItem}
        onClose={() => setProductToRemove(undefined)}
        onDelete={async () => {
          if (productToRemove) {
            const cartUpdated = await removeLineItem(productToRemove.productId);
            //update braze attr cart_items
            if (cartUpdated?.lineItems) {
              syncCartItemsUserAttribute(cartUpdated.lineItems);
            }
            const skusHistory = removeSkuFromHistory(
              productToRemove.productSKU
            );

            const lineItem = cart?.lineItems?.find(
              (item) => item?.id === productToRemove.productId
            );

            if (lineItem) {
              const productData = getOrderCompletedOnlineProduct(lineItem);
              trackEvent('product_removed_from_cart', productData);
            }

            setProductToRemove(undefined);
            await refetchShippingMethods(skusHistory, cartUpdated);
            handleSetDefaultOptions();
          }
        }}
      />
      <div className="co-h-[50px] tablet_w:co-hidden"></div>
    </div>
  );
};

const ShippingPage = () => {
  return (
    <ShippingPageContext>
      <ShippingPageContent />
    </ShippingPageContext>
  );
};

export default ShippingPage;
